import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './lib/hooks';
import { Layout } from './components/Layout';
import { VersionBadge } from './components/VersionBadge';
import { AuthPage } from './pages/AuthPage';
import { CheckEmailPage } from './pages/CheckEmailPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { TodayPage } from './pages/TodayPage';
import { RunStackPage } from './pages/RunStackPage';
import { StacksPage } from './pages/StacksPage';
import { StackDetailPage } from './pages/StackDetailPage';
import { CreateEditStackPage } from './pages/CreateEditStackPage';
import { HistoryPage } from './pages/HistoryPage';
import { SettingsPage } from './pages/SettingsPage';

// Protected route wrapper
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/auth" replace />;
    }
    
    return children;
}

// Public route - redirect if logged in
function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/auth" element={
                <PublicRoute>
                    <AuthPage />
                </PublicRoute>
            } />

            <Route path="/check-email" element={<CheckEmailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={
                <PublicRoute>
                    <ForgotPasswordPage />
                </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<TodayPage />} />
                <Route path="stacks" element={<StacksPage />} />
                <Route path="stacks/:id" element={<StackDetailPage />} />
                <Route path="stacks/:id/edit" element={<CreateEditStackPage />} />
                <Route path="stacks/new" element={<CreateEditStackPage />} />
                <Route path="history" element={<HistoryPage />} />
                <Route path="settings" element={<SettingsPage />} />
            </Route>
            
            <Route path="/run/:id" element={
                <ProtectedRoute>
                    <RunStackPage />
                </ProtectedRoute>
            } />
        </Routes>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <div className="min-h-screen bg-background">
                    <AppRoutes />
                    <Toaster position="bottom-center" />
                    <VersionBadge />
                </div>
            </AuthProvider>
        </Router>
    );
}

export default App;
